const barcinoRecipeDrawer = {
    _initialized: false,
    _currentSlug: '',

    init() {
        if (this._initialized) return;
        this._initialized = true;

        const drawer = this._drawer();
        if (!drawer) return;

        document.addEventListener('click', (event) => {
            const trigger = event.target.closest('[data-recipe-trigger]');
            if (!trigger) return;

            const slug = trigger.dataset.recipeSlug || '';
            if (!slug) return;

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            this.open(slug);
        }, true);

        drawer.querySelectorAll('[data-recipe-close]').forEach((element) => {
            element.addEventListener('click', () => this.close());
        });

        drawer.querySelectorAll('[data-recipe-nav]').forEach((button) => {
            button.addEventListener('click', () => {
                const slug = button.dataset.recipeSlug || '';
                if (slug) this.load(slug);
            });
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
                this.close();
            }
        });
    },

    open(slug) {
        const drawer = this._drawer();
        if (!drawer) return;

        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        drawer.inert = false;
        document.body.classList.add('has-recipe-drawer-open');
        this.load(slug);
    },

    close() {
        const drawer = this._drawer();
        if (!drawer) return;

        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        drawer.inert = true;
        document.body.classList.remove('has-recipe-drawer-open');
        this._pauseMedia(drawer);
    },

    async load(slug) {
        const drawer = this._drawer();
        if (!drawer) return;

        this._currentSlug = slug;
        this._setLoading(drawer);

        try {
            const response = await fetch(drawer.dataset.recipeAjaxBase + encodeURIComponent(slug), {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            const data = await response.json();

            if (!data.success || !data.recipe) {
                this._setError(drawer);
                return;
            }

            this._render(drawer, data.recipe);
        } catch {
            this._setError(drawer);
        }
    },

    _drawer() {
        return document.getElementById('recipe-drawer');
    },

    _setLoading(drawer) {
        const title = drawer.querySelector('[data-recipe-title]');
        const description = drawer.querySelector('[data-recipe-description]');
        const media = drawer.querySelector('[data-recipe-media]');
        const params = drawer.querySelector('[data-recipe-params]');
        const message = drawer.querySelector('[data-recipe-message]');

        if (title) title.textContent = '';
        if (description) {
            description.innerHTML = '';
            description.hidden = true;
        }
        if (media) media.replaceChildren();
        if (media) media.hidden = true;
        if (params) params.replaceChildren();
        drawer.querySelectorAll('[data-recipe-nav]').forEach((button) => {
            button.hidden = true;
            delete button.dataset.recipeSlug;
        });

        if (message) {
            message.textContent = drawer.dataset.labelLoading || '';
            message.hidden = false;
        }
    },

    _setError(drawer) {
        const message = drawer.querySelector('[data-recipe-message]');
        if (message) {
            message.textContent = drawer.dataset.labelError || '';
            message.hidden = false;
        }
    },

    _render(drawer, recipe) {
        const title = drawer.querySelector('[data-recipe-title]');
        const description = drawer.querySelector('[data-recipe-description]');
        const media = drawer.querySelector('[data-recipe-media]');
        const params = drawer.querySelector('[data-recipe-params]');
        const message = drawer.querySelector('[data-recipe-message]');

        if (title) title.textContent = recipe.title || '';
        if (description) {
            description.innerHTML = recipe.description || '';
            description.hidden = !recipe.description;
        }
        if (message) {
            message.textContent = '';
            message.hidden = true;
        }

        if (media) {
            media.replaceChildren();
            const mediaNode = this._buildMedia(drawer, recipe);
            media.hidden = !mediaNode;
            if (mediaNode) media.appendChild(mediaNode);
        }

        if (params) {
            params.replaceChildren();
            (recipe.params || []).forEach((param) => {
                const row = document.createElement('div');
                row.className = 'recipe-drawer__param-row';

                const dt = document.createElement('dt');
                dt.className = '__body-02';
                dt.textContent = param.name || '';

                const dd = document.createElement('dd');
                dd.className = '__body-02 _medium';
                dd.textContent = param.value || '';

                row.append(dt, dd);
                params.appendChild(row);
            });
        }

        this._renderNav(drawer, recipe.navigation || {});
    },

    _buildMedia(drawer, recipe) {
        if (recipe.video_url) {
            const embedUrl = this._embedUrl(recipe.video_url);
            if (embedUrl) {
                const iframe = document.createElement('iframe');
                iframe.src = embedUrl;
                iframe.title = drawer.dataset.labelVideoTitle || '';
                iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
                iframe.allowFullscreen = true;
                iframe.loading = 'lazy';
                return iframe;
            }

            const video = document.createElement('video');
            video.src = recipe.video_url;
            video.controls = true;
            video.playsInline = true;
            if (recipe.photo?.large) video.poster = recipe.photo.large;
            return video;
        }

        if (!recipe.photo?.large) return null;

        const picture = document.createElement('picture');
        if (recipe.photo.large_webp) {
            const source = document.createElement('source');
            source.srcset = recipe.photo.large_webp;
            source.type = 'image/webp';
            picture.appendChild(source);
        }

        const img = document.createElement('img');
        img.src = recipe.photo.large;
        img.alt = recipe.title || '';
        img.loading = 'lazy';
        picture.appendChild(img);
        return picture;
    },

    _renderNav(drawer, navigation) {
        ['previous', 'next'].forEach((direction) => {
            const button = drawer.querySelector(`[data-recipe-nav="${direction}"]`);
            const item = navigation[direction];
            if (!button) return;

            if (item?.slug) {
                button.dataset.recipeSlug = item.slug;
                button.hidden = false;
            } else {
                button.hidden = true;
                delete button.dataset.recipeSlug;
            }
        });
    },

    _embedUrl(url) {
        try {
            const parsed = new URL(url, window.location.origin);
            if (parsed.hostname.includes('youtube.com')) {
                const id = parsed.searchParams.get('v');
                return id ? `https://www.youtube.com/embed/${id}` : url;
            }
            if (parsed.hostname.includes('youtu.be')) {
                const id = parsed.pathname.replace('/', '');
                return id ? `https://www.youtube.com/embed/${id}` : url;
            }
            if (parsed.hostname.includes('vimeo.com')) {
                const id = parsed.pathname.split('/').filter(Boolean).pop();
                return id ? `https://player.vimeo.com/video/${id}` : url;
            }
            return null;
        } catch {
            return null;
        }
    },

    _pauseMedia(drawer) {
        drawer.querySelectorAll('video').forEach((video) => video.pause());
        drawer.querySelectorAll('iframe').forEach((iframe) => {
            iframe.src = iframe.src;
        });
    },
};

export default barcinoRecipeDrawer;
